import { useCallback, useEffect, useRef, useState } from 'react';
import ExtraIcons, {
  ALL_CATEGORIES,
  BALLOONS,
  TEA_COFFEE,
  EDIBLE_BOUQUETS,
  FRESH_FLOWERS,
  INDOOR_PLANTS,
  PARTY_DECOR,
  BAKED_GOODS,
  CANDY_AND_SWEETS,
  BEAUTY,
  GOURMET_SETS,
  HOME_DECOR,
  TABLEWARE,
  CUSTOM_GIFTS,
  GIFT_SETS,
} from '../ExtraIcons/ExtraIcons';
import css from './CategorySelector.module.css';

const categories = [
  { name: 'All categories', key: 'all', iconName: ALL_CATEGORIES },
  { name: 'Flowers', key: 'Fresh_Flowers', iconName: FRESH_FLOWERS },
  { name: 'Edible Bouquets', key: 'Edible_Bouquets', iconName: EDIBLE_BOUQUETS },
  { name: 'Baked Goods', key: 'Baked_Goods', iconName: BAKED_GOODS },
  { name: 'Candy & Sweets', key: 'Candy_Sweets', iconName: CANDY_AND_SWEETS },
  { name: 'Balloons', key: 'Balloons', iconName: BALLOONS },
  {
    name: 'Decor',
    key: 'Party_Decor',
    iconName: PARTY_DECOR,
  },
  {
    name: 'Gourmet Sets',
    key: 'Gourmet_Sets',
    iconName: GOURMET_SETS,
  },
  {
    name: 'Custom Gifts',
    key: 'Custom_Gifts',
    iconName: CUSTOM_GIFTS,
  },
  {
    name: 'Gift Sets',
    key: 'Gift_Sets',
    iconName: GIFT_SETS,
  },
  {
    name: 'Home Decor',
    key: 'Home_Decor',
    iconName: HOME_DECOR,
  },
  { name: 'Indoor Plants', key: 'Indoor_Plants', iconName: INDOOR_PLANTS },
  { name: 'Beauty', key: 'Beauty', iconName: BEAUTY },
  {
    name: 'Tea & Coffee',
    key: 'Tea_Coffee',
    iconName: TEA_COFFEE,
  },
  { name: 'Tableware', key: 'Tableware', iconName: TABLEWARE },
];

// Resolve which chip should be highlighted from the active category filter.
// Prefer the explicit prop (available on both server and client — it's derived
// from the request's URL params), and fall back to reading the URL directly so
// a deep link (e.g. an SEO page's "Browse decor" button →
// /s?pub_categoryLevel1=Party_Decor) still highlights the right chip.
const activeIdxFromCategory = activeCategory => {
  let key = activeCategory;
  if (!key && typeof window !== 'undefined') {
    key = new URL(window.location.href).searchParams.get('pub_categoryLevel1');
  }
  const index = categories.findIndex(cat => cat.key === key);
  return index === -1 ? 0 : index;
};

const CategorySelector = ({ getHandleChangedValueFn, activeCategory }) => {
  // Initialise from the prop (not from `window`) so the server renders the
  // correct chip too. Starting at 0 on the server and patching it in an effect
  // left deep links stuck on "All categories" whenever the effect didn't win.
  const [activeIdx, setActiveIdx] = useState(() => activeIdxFromCategory(activeCategory));

  // Keep the highlight in sync with the active filter across re-renders (deep
  // link landing, clicking a category, the map recentring, etc.).
  useEffect(() => {
    setActiveIdx(activeIdxFromCategory(activeCategory));
  }, [activeCategory]);
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check if scrollable content overflows
  const checkOverflow = useCallback(() => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
      setShowRightArrow(scrollWidth > clientWidth && scrollLeft + clientWidth < scrollWidth);
      setShowLeftArrow(scrollLeft > 0);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      window.requestAnimationFrame(checkOverflow);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkOverflow]);

  const handleScrollRight = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;
      const left = Math.min(scrollLeft + clientWidth, maxScrollLeft);

      scrollRef.current.scrollTo({
        left,
        behavior: 'smooth',
      });
      setTimeout(checkOverflow, 300);
    }
  };

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;

      scrollRef.current.scrollTo({
        left: Math.max(0, scrollLeft - clientWidth),
        behavior: 'smooth',
      });
      setTimeout(checkOverflow, 300);
    }
  };

  const handleCategoryClick = idx => {
    const cat = categories[idx];
    setActiveIdx(idx);

    const url = new URL(window.location);
    const searchParams = url.searchParams;

    if (cat.key !== 'all') {
      searchParams.set('pub_categoryLevel1', cat.key);
    } else {
      searchParams.delete('pub_categoryLevel1');
    }

    window.history.pushState({}, '', url.toString());

    getHandleChangedValueFn(true)({
      pub_categoryLevel1: cat.key === 'all' ? null : cat.key,
    });
  };

  return (
    <div className={css.categorySelectorWrapper}>
      {showLeftArrow && (
        <button className={`${css.arrowBtn} ${css.leftArrow}`} onClick={handleScrollLeft}>
          &lt;
        </button>
      )}
      <div className={css.categoryListOuter} ref={scrollRef} onScroll={checkOverflow}>
        <div className={css.categoryList}>
          {categories.map((cat, idx) => (
            <div
              key={cat.name}
              className={`${idx === activeIdx ? css.categoryItemActive : ''} ${css.categoryItem}`}
              onClick={() => handleCategoryClick(idx)}
            >
              <ExtraIcons
                icon={cat.iconName}
                className={`${idx === activeIdx ? css.iconActive : ''} ${css.icon}`}
              />
              <span className={`${idx === activeIdx ? css.nameActive : ''} ${css.name}`}>
                {cat.name}
              </span>
            </div>
          ))}
        </div>
      </div>
      {showRightArrow && (
        <button className={`${css.arrowBtn} ${css.rightArrow}`} onClick={handleScrollRight}>
          &gt;
        </button>
      )}
    </div>
  );
};

export default CategorySelector;
